import type { Emoji } from 'emojibase';
import type { ReactNode } from 'react';

import { useEffect, useRef, useState } from 'react';

import { currentLocale } from '~/utils';

import {
  AutocompleteTextarea,
  type AutocompleteTextareaRenderProps,
} from '../AutocompleteTextarea/AutocompleteTextarea';
import {
  insertAutocompleteSuggestion,
  updateAutocompleteQuery,
} from '../AutocompleteTextarea/utils';

import { getTriggerRange, loadEmojiData } from './utils';

function sortByDefaultOrder(firstEmoji: Emoji, secondEmoji: Emoji) {
  return (
    (firstEmoji.order ?? Number.MAX_SAFE_INTEGER) -
    (secondEmoji.order ?? Number.MAX_SAFE_INTEGER)
  );
}

export type EmojiAutocompleteTextareaProps = {
  children: (props: AutocompleteTextareaRenderProps) => ReactNode;
  onChange: (value: string) => void;
};

function getEmojiKey(emoji: Emoji) {
  return emoji.emoji;
}

function renderEmojiOption(emoji: Emoji) {
  return (
    <>
      <span aria-hidden="true" className="text-lg">
        {emoji.emoji}
      </span>
      <span className="min-w-0 truncate">{emoji.label}</span>
    </>
  );
}

function useEmojiSuggestions(query: string | null, locale: string) {
  const [suggestions, setSuggestions] = useState<Emoji[]>([]);

  useEffect(() => {
    if (query === null) return;
    let active = true;
    void loadEmojiData(locale)
      .then((emojis) => {
        if (!active) return;
        const normalizedQuery = query.toLowerCase();
        const nextSuggestions = normalizedQuery
          ? emojis.filter((emoji) =>
              [emoji.label, ...(emoji.shortcodes ?? []), ...(emoji.tags ?? [])]
                .join(' ')
                .toLowerCase()
                .includes(normalizedQuery),
            )
          : [...emojis].sort(sortByDefaultOrder);
        setSuggestions(nextSuggestions);
      })
      .catch(() => {
        if (!active) return;
        setSuggestions([]);
      });
    return () => {
      active = false;
    };
  }, [locale, query]);

  return suggestions;
}

export function EmojiAutocompleteTextarea({
  children,
  onChange,
}: EmojiAutocompleteTextareaProps) {
  const locale = currentLocale();
  const [query, setQuery] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLTextAreaElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const suggestions = useEmojiSuggestions(query, locale);
  const isOpen = query !== null && suggestions.length > 0;

  function close() {
    setQuery(null);
    setAnchorEl(null);
  }

  function selectEmoji(emoji: Emoji) {
    return insertAutocompleteSuggestion({
      getTriggerRange,
      onChange,
      replacement: emoji.emoji,
      textarea: textareaRef.current,
    });
  }
  return (
    <AutocompleteTextarea
      anchorEl={anchorEl}
      getOptionKey={getEmojiKey}
      isOpen={isOpen}
      onChange={(event) => {
        onChange(event.target.value);
        updateAutocompleteQuery(event, getTriggerRange, setAnchorEl, setQuery);
      }}
      onClose={close}
      onSelect={selectEmoji}
      options={suggestions}
      renderOption={renderEmojiOption}
      textareaRef={textareaRef}
    >
      {children}
    </AutocompleteTextarea>
  );
}
